import {
  LogicConditionTypeEnum,
  MultipleChoice,
  NumberCondition,
  ReportNode,
  ResponseDatetimeSubTypeEnum,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { UUID } from 'src/exShared/util/uuid'

const getDeleteNodeIds = (
  targetNodeId: number,
  templateNodes: { [key: number]: TemplateNodeSchema },
) => {
  let nodeIds: number[] = [targetNodeId]
  const targetNode = templateNodes[targetNodeId]
  if (targetNode?.nodes?.length !== 0) {
    targetNode?.nodes?.forEach(
      (nodeId: number) =>
        (nodeIds = nodeIds.concat(getDeleteNodeIds(nodeId, templateNodes))),
    )
  }
  return nodeIds
}

const createNewTemplateNode = (
  id: number,
  type: TemplateNodeTypeEnum,
): TemplateNodeSchema => {
  return {
    id: id,
    uuid: UUID(),
    type: type,
    nodes: [],
  }
}

const createNewLogicNode = (
  id: number,
  conditionType: LogicConditionTypeEnum,
  responseMultipleChoices: Array<MultipleChoice>,
): TemplateNodeSchema => {
  return {
    ...createNewTemplateNode(id, TemplateNodeTypeEnum.Logic),
    logic: {
      conditionType,
      responseMultipleChoices,
    },
  }
}

const createNewNumberLogicNode = (
  id: number,
  numberCondition: NumberCondition,
): TemplateNodeSchema => {
  return {
    ...createNewTemplateNode(id, TemplateNodeTypeEnum.Logic),
    logic: {
      numberConditions: [numberCondition],
    },
  }
}

/** 新しく作る条件分岐ノードの id の起点(未使用の最小 id)。 */
const getNextLogicNodeId = (templateNodes: TemplateNodesDict): number =>
  Math.max(...Object.keys(templateNodes).map(Number), 0) + 1

/**
 * 選択肢質問に条件分岐ノードを追加する。
 *
 * - 「選択したそれぞれの項目で分岐する」(Is): 選択肢1つにつき条件分岐を1つ作る
 * - 「まとめて一つの質問へ分岐する」(IsOneOf): 全選択肢をまとめた条件分岐を1つ作る
 */
export const addMultipleChoiceLogicNodes = (
  templateNodes: TemplateNodesDict,
  targetNode: TemplateNodeSchema,
  conditionType: LogicConditionTypeEnum,
  responseMultipleChoices: MultipleChoice[],
): TemplateNodesDict => {
  const nextNodes = { ...templateNodes }
  const target = nextNodes[targetNode.id]
  if (!target) {
    return templateNodes
  }

  let nextLogicId = getNextLogicNodeId(nextNodes)
  const logicNodes =
    conditionType === LogicConditionTypeEnum.Is
      ? // 「選択したそれぞれの項目で分岐する」の場合
        responseMultipleChoices.map(choice => {
          const logicNode = createNewLogicNode(nextLogicId, conditionType, [
            choice,
          ])
          nextLogicId += 1
          return logicNode
        })
      : // 「まとめて一つの質問へ分岐する」の場合
        [
          createNewLogicNode(
            nextLogicId,
            conditionType,
            responseMultipleChoices,
          ),
        ]

  logicNodes.forEach(logicNode => {
    nextNodes[logicNode.id] = logicNode
  })
  nextNodes[target.id] = {
    ...target,
    nodes: [...target.nodes, ...logicNodes.map(logicNode => logicNode.id)],
  }
  return nextNodes
}

/**
 * 選択肢質問の条件分岐を、選択された選択肢の集合に合わせて更新する。
 *
 * 分岐の条件種別(conditionType)が変わったかどうかで挙動が分かれる。
 * - 条件種別が変わった: 既存の条件分岐をすべて捨てて作り直す
 * - 条件種別が同じで Is: 選択肢の増減に追従する(残る分は既存ノードを維持し、
 *   選択が外れた分だけ削除、増えた分だけ追加する)
 * - 条件種別が同じで IsOneOf: 条件分岐は1つのままなので、選択肢一覧だけ差し替える
 */
export const updateMultipleChoiceLogicNodes = (
  templateNodes: TemplateNodesDict,
  targetNode: TemplateNodeSchema,
  conditionType: LogicConditionTypeEnum,
  responseMultipleChoices: MultipleChoice[],
): TemplateNodesDict => {
  const nextNodes = { ...templateNodes }
  const target = nextNodes[targetNode.id]
  if (!target) {
    return templateNodes
  }

  // 既存のロジックノード
  const oldLogicNodes = target.nodes
    .map(nodeId => nextNodes[nodeId])
    .filter(
      (node): node is TemplateNodeSchema =>
        node?.type === TemplateNodeTypeEnum.Logic,
    )
  // 既存のconditionType(引数の conditionType が更新後の値)
  const oldConditionType = oldLogicNodes[0]?.logic?.conditionType

  // 条件種別が変わった場合は、既存のロジックを削除してから作り直す
  if (oldConditionType !== conditionType) {
    oldLogicNodes.forEach(node => {
      delete nextNodes[node.id]
    })
    const nodesWithoutOldLogics = {
      ...nextNodes,
      [target.id]: {
        ...target,
        nodes: target.nodes.filter(
          nodeId => !oldLogicNodes.some(logic => logic.id === nodeId),
        ),
      },
    }
    const targetWithoutOldLogics = nodesWithoutOldLogics[target.id]
    if (!targetWithoutOldLogics) {
      return templateNodes
    }
    return addMultipleChoiceLogicNodes(
      nodesWithoutOldLogics,
      targetWithoutOldLogics,
      conditionType,
      responseMultipleChoices,
    )
  }

  // 「選択したそれぞれの項目で分岐する」の場合
  if (conditionType === LogicConditionTypeEnum.Is) {
    // 更新後(最新)の条件式のid一覧
    const selectedChoiceIds = new Set(
      responseMultipleChoices.map(choice => choice.id),
    )
    // 更新後にも残る既存のロジックノード
    const retainedLogicNodes = oldLogicNodes.filter(logic => {
      const choiceId = logic.logic?.responseMultipleChoices?.[0]?.id
      return choiceId !== undefined && selectedChoiceIds.has(choiceId)
    })
    // 既存から不要なロジックを削除
    oldLogicNodes
      .filter(logic => !retainedLogicNodes.includes(logic))
      .forEach(logic => {
        delete nextNodes[logic.id]
      })

    // 更新が不要(既存にもあって、更新後にもある)な条件式のid一覧
    const retainedChoiceIds = new Set(
      retainedLogicNodes
        .map(logic => logic.logic?.responseMultipleChoices?.[0]?.id)
        .filter(id => id !== undefined),
    )
    const nodesWithRetainedLogics = {
      ...nextNodes,
      [target.id]: {
        ...target,
        nodes: target.nodes.filter(
          nodeId =>
            !oldLogicNodes.some(logic => logic.id === nodeId) ||
            retainedLogicNodes.some(logic => logic.id === nodeId),
        ),
      },
    }
    const targetWithRetainedLogics = nodesWithRetainedLogics[target.id]
    if (!targetWithRetainedLogics) {
      return templateNodes
    }
    // 新規に増えた選択肢の分だけロジックを追加する
    return addMultipleChoiceLogicNodes(
      nodesWithRetainedLogics,
      targetWithRetainedLogics,
      conditionType,
      responseMultipleChoices.filter(
        choice => choice.id === undefined || !retainedChoiceIds.has(choice.id),
      ),
    )
  }

  // 「まとめて一つの質問へ分岐する」の場合。ロジックは1つのままで選択肢一覧だけ差し替える
  const logicNode = oldLogicNodes[0]
  if (!logicNode?.logic) {
    return templateNodes
  }
  nextNodes[logicNode.id] = {
    ...logicNode,
    logic: {
      ...logicNode.logic,
      responseMultipleChoices,
    },
  }
  return nextNodes
}

/**
 * 数値質問に条件分岐ノードを追加する。条件(正常/違反)1つにつき条件分岐を1つ作る。
 */
export const addNumberLogicNodes = (
  templateNodes: TemplateNodesDict,
  targetNode: TemplateNodeSchema,
  numberConditions: NumberCondition[],
): TemplateNodesDict => {
  const nextNodes = { ...templateNodes }
  const target = nextNodes[targetNode.id]
  if (!target) {
    return templateNodes
  }

  let nextLogicId = getNextLogicNodeId(nextNodes)
  const logicNodes = numberConditions.map(condition => {
    const logicNode = createNewNumberLogicNode(nextLogicId, condition)
    nextLogicId += 1
    return logicNode
  })
  logicNodes.forEach(logicNode => {
    nextNodes[logicNode.id] = logicNode
  })
  nextNodes[target.id] = {
    ...target,
    nodes: [...target.nodes, ...logicNodes.map(logicNode => logicNode.id)],
  }
  return nextNodes
}

/**
 * 数値質問の条件分岐を、選択された条件(正常/違反)の集合に合わせて更新する。
 * 残る条件の条件分岐ノードは維持し、外れた分だけ削除、増えた分だけ追加する。
 */
export const updateNumberLogicNodes = (
  templateNodes: TemplateNodesDict,
  targetNode: TemplateNodeSchema,
  numberConditions: NumberCondition[],
): TemplateNodesDict => {
  const nextNodes = { ...templateNodes }
  const target = nextNodes[targetNode.id]
  if (!target) {
    return templateNodes
  }

  // 既存のロジックノード
  const oldLogicNodes = target.nodes
    .map(nodeId => nextNodes[nodeId])
    .filter(
      (node): node is TemplateNodeSchema =>
        node?.type === TemplateNodeTypeEnum.Logic,
    )
  // 更新後(最新)のlogicType一覧
  const selectedLogicTypes = new Set(
    numberConditions.map(condition => condition.logicType),
  )
  // 更新後にも残る既存のロジックノード
  const retainedLogicNodes = oldLogicNodes.filter(logic => {
    const logicType = logic.logic?.numberConditions?.[0]?.logicType
    return logicType !== undefined && selectedLogicTypes.has(logicType)
  })
  // 既存から不要なロジックを削除
  oldLogicNodes
    .filter(logic => !retainedLogicNodes.includes(logic))
    .forEach(logic => {
      delete nextNodes[logic.id]
    })

  // 更新が不要(既存にもあって、更新後にもある)なlogicType一覧
  const retainedLogicTypes = new Set(
    retainedLogicNodes
      .map(logic => logic.logic?.numberConditions?.[0]?.logicType)
      .filter(logicType => logicType !== undefined),
  )
  const nodesWithRetainedLogics = {
    ...nextNodes,
    [target.id]: {
      ...target,
      nodes: target.nodes.filter(
        nodeId =>
          !oldLogicNodes.some(logic => logic.id === nodeId) ||
          retainedLogicNodes.some(logic => logic.id === nodeId),
      ),
    },
  }
  const targetWithRetainedLogics = nodesWithRetainedLogics[target.id]
  if (!targetWithRetainedLogics) {
    return templateNodes
  }
  // 新規に増えた条件の分だけロジックを追加する
  return addNumberLogicNodes(
    nodesWithRetainedLogics,
    targetWithRetainedLogics,
    numberConditions.filter(
      condition => !retainedLogicTypes.has(condition.logicType),
    ),
  )
}

/**
 * 再帰的に子ノードの ID を更新する
 * @param children ID を更新したい子ノード
 * @param currentId 現在雛形内に存在する最大の nodeId
 * @param oldUUIDMappings コピー元の UUID とコピー後の UUID のマッピング
 * @returns 更新後の現在雛形内に存在する最大の nodeId
 */
const walkAndUpdateId = (
  children: ReportNode[],
  currentId: number,
  oldUUIDMappings: { [key: string]: string },
): number => {
  if (!children || children.length === 0) {
    return currentId
  }
  children.forEach((child: ReportNode) => {
    child.id = ++currentId
    const uuid = UUID()
    oldUUIDMappings[child.uuid] = uuid
    child.uuid = uuid
    currentId = walkAndUpdateId(child.nodes, currentId, oldUUIDMappings)
  })
  return currentId
}

/**
 * node (TemplateNodeSchema)を deepCopy した場合に、
 * コピー先では不要となるプロパティを削除する
 *
 * @param node
 */
const deleteUnnecessaryCopiedProperties = (node: TemplateNodeSchema) => {
  delete node.section?.id
  delete node.question?.id
  delete node.logic?.id
  delete node.createdAt
}

/**
 * WARN: この関数は、引数 templateNodes 内の時間計測ノードを破壊的に変更する
 *
 * 引数 templateNodes 内の時間計測ノードの参照先になっている開始日時質問ノードの subType を取得し、
 * それを時間計測ノードの subType に設定する
 *
 * 参照先の開始質問と終了質問の subType が一致していることを前提としているため、
 * 意図的に、開始質問の subType しか見ないようにしている
 *
 * @param templateNodes ノードのマップ。関数内で破壊的に変更される
 */
const alignResponseTimeMeasurements = (templateNodes: {
  [key: number]: TemplateNodeSchema
}) => {
  /** 日時質問と subType の対応関係を表すマップ */
  const dtMap = Object.values(templateNodes).reduce<
    Map<
      string,
      ResponseDatetimeSubTypeEnum.TIME | ResponseDatetimeSubTypeEnum.DATETIME
    >
  >((prev, curr) => {
    const uuid = curr.uuid
    const dt = curr.question?.responseDatetimes?.[0]
    const subType = dt?.subType
    // 仕様上、時間計測の参照先となる日時質問の subType が日付になることはないのでガードする
    const isDate = subType === ResponseDatetimeSubTypeEnum.DATE
    if (!uuid || !dt || !subType || isDate) return prev

    prev.set(uuid, subType)
    return prev
  }, new Map<string, ResponseDatetimeSubTypeEnum.TIME | ResponseDatetimeSubTypeEnum.DATETIME>())

  for (const node of Object.values(templateNodes)) {
    // 時間計測ノードに対する変更しかしないので、それ以外の種別ならスキップする
    const tm = node.question?.responseTimeMeasurements?.[0]
    if (!tm) continue

    // 開始質問の subType だけ見れば良い
    const startQuestionNode = dtMap.get(tm.startQuestionNodeUUID)
    if (!startQuestionNode) {
      // この時間計測ノードが参照している日時ノードが存在しない、ということは通常ありえないが、
      // そうなった場合は、subType を日時に設定しておく
      console.error(
        `startQuestionNode is not found. set TimeMeasurement(id=${tm.id})'s subType to DATETIME`,
      )
    }

    tm.subType =
      dtMap.get(tm.startQuestionNodeUUID) ||
      ResponseDatetimeSubTypeEnum.DATETIME
  }

  return templateNodes
}

export {
  alignResponseTimeMeasurements,
  createNewLogicNode,
  createNewNumberLogicNode,
  deleteUnnecessaryCopiedProperties,
  getDeleteNodeIds,
  walkAndUpdateId,
}
